/**
 * SignInMask
 * Full-screen overlay that persists across the Google redirect sign-in flow,
 * masking all intermediate page hops (ProtectedRoute spinner → Index spinner → home).
 *
 * Only activates when `sessionStorage` has the 'btSigningIn' flag, which is set
 * just before calling signInWithRedirect in AuthContext. This prevents the mask
 * from showing on normal app opens for already-logged-in users.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export const SIGN_IN_FLAG = 'btSigningIn';

const SignInMask = () => {
  const { loading } = useAuth();

  // Capture flag once at mount — if the page loaded because of a redirect,
  // the flag is already in sessionStorage before React even boots.
  const [active] = useState(() => {
    if (typeof sessionStorage === 'undefined') return false;
    const ts = parseInt(sessionStorage.getItem(SIGN_IN_FLAG) ?? '0', 10);
    if (!ts) return false;
    // Expire after 30 seconds — stale flag from a force-closed iOS PWA
    if (Date.now() - ts > 30_000) {
      sessionStorage.removeItem(SIGN_IN_FLAG);
      return false;
    }
    return true;
  });

  const [visible, setVisible] = useState(active);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!active) return;
    if (!loading) {
      // Auth resolved — start fade out, then unmount
      setFading(true);
      const t = setTimeout(() => setVisible(false), 500);
      return () => clearTimeout(t);
    }
  }, [loading, active]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[999] flex flex-col items-center justify-center px-6"
      style={{
        background: 'linear-gradient(135deg, #FFF5F5 0%, #FFF0F0 50%, #FFE8E8 100%)',
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      {/* numaa logo — same as Login page */}
      <div className="w-40 h-40 mb-8 flex items-center justify-center">
        <img
          src="/numaa_logo_nobg.png"
          alt="numaa"
          className="w-full h-full object-contain animate-pulse"
        />
      </div>

      {/* Status */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-3">Every Moment Matters</h1>
        <div className="flex items-center justify-center gap-2.5 text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-200 border-t-orange-400 rounded-full animate-spin flex-shrink-0" />
          <span className="text-sm font-medium">Signing you in…</span>
        </div>
      </div>
    </div>
  );
};

export default SignInMask;
